import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ExecutionLimitWarningPopupProps {
  limit: number;
  currentCount: number;
  onContinue: () => void;
  onCancel: () => void;
}

export const ExecutionLimitWarningPopup: React.FC<ExecutionLimitWarningPopupProps> = ({
  limit,
  currentCount,
  onContinue,
  onCancel
}) => {
  return (
    <div data-component="ExecutionLimitWarningPopup" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Execution Limit Reached</h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-700 mb-4">
            This evaluation test has reached its execution limit.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-700">Execution Limit:</span>
              <span className="font-semibold text-gray-900">{limit}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-700">Current Count:</span>
              <span className="font-semibold text-gray-900">{currentCount}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            You can continue executing this test, but it has already reached the recommended limit. 
            Additional executions will still be tracked and included in analytics.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onContinue}
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Continue Anyway
          </button>
        </div>
      </div>
    </div>
  );
};
